import { camelCaseToSpacesRegEx } from "../../NodePropertiesForm/FormConstants";
import { Node } from "reactflow";
import { AnchorArea } from "../../Util/AnchorUtil";
import { DataType } from "../TypeDefinitions";
import { NodeConfigPropertyType } from "../../FlowDocument/PropertyTypeDefinitions";

/** The category a node type is defined by. */
/** The order here dictates the order in which categories are displayed. */
export enum NodeTypeCategory {
  general,
  transformations,
  ai,
  services,
  lists,
  logic,
  dates,
  text,
  numbers
}

export enum NodeSize {
  Normal = 200,
  Small = 160,
  Large = 250
}

export interface IDataParam {
  name: string;
  type: DataType;
  schemaId?: string;
  enabled?: boolean;
  required?: boolean;
  label?: string;
  hideLabel?: boolean;
  metadata?: Record<string, any> | null;
  /**
   * When true, this input accepts any object schema and maps the connected object into the
   * node's target schema (an "object mapping input"). This is the designer mirror of the
   * backend source of truth `DataInputModel.MapsObject`, stamped from the
   * `[ObjectMappingInput]` annotation, which is the surface that becomes the OAS node spec.
   *
   * Because the connected object is mapped rather than schema-matched, changing the schema
   * selection of a picker linked to this input must not delete the input's connection.
   */
  mapsObject?: boolean;
}

export interface IEnumOptions {
  values: any;
}

export interface IDataPropertyCountOptions {
  cloneFrom: string;
}

export enum ReferencePropertyType {
  identifier,
  includedField
}

export interface IDataTypeOptions {
  dataTypes?: DataType[];
  dataProperties: string[];
  referenceProperty?: ReferencePropertyType;
}

export interface IConnectedSchemaTagsOptions {
  dataParams: string[];
}

export interface ISchemaPickerOptions {
  displayProperty: string;
  referenceOnly?: boolean;
  excludeReferences?: boolean;
  includePrimitives?: boolean;
  includeLists?: boolean;
  linkedOutput?: string;
  dataProperties: string[];
  changeSchemaOnConnection?: boolean;
}

export interface ISchemaPropertyPickerOptions {
  schemaIdProperty: string;
  displayProperty: string;
  identifierOnly?: boolean;
}

export interface IStandardObjectPickerOptions extends ISourceApiPickerOptions {
  customFieldsOnly: boolean;
}

export interface ISourceApiPickerWithChildrenOptions extends ISourceApiPickerOptions {
  clearPropertiesOnChange?: string[];
}

export interface IEventPropertyOptions extends ISourceApiPickerWithChildrenOptions {
  setFlowInputsFromSchemas: boolean;
}

export interface IActionEventPropertyOptions extends ISourceApiPickerOptions {
  eventIdProperty: string;
}

export interface ISourceApiPickerOptions {
  multiSelect?: boolean;
}

export interface IFlowInputEditorOptions {
  inputSubName: string;
  excludedProperties: string[];
  applyOverrides?: (node: Node<any>, flowInput: any) => void;
  label?: string;
  defaultValue?: any;
}

export interface ITextOptions {
  /** Renders the text property as a multi-line, auto-growing text area instead of a single-line input. */
  multiline?: boolean;
}

export interface ITemplateEditorOptions extends ITextOptions {
  /** Name of the input/output data param whose type and schema drive the available tokens. */
  dataParam: string;
}

export enum IoType {
  get,
  search,
  update,
  add,
  delete
}

export enum DocumentSchemaSource {
  customObject = "CUSTOM_OBJECT",
  dataSource = "DATA_SOURCE",
  plexOpenApi = "PLEX_OPEN_API",
  customFieldsV1 = "CUSTOM_FIELDS_V1",
  plexEvent = "PLEX_EVENT",
  schemaPicker = "SCHEMA_PICKER",
  externalApi = "EXTERNAL_API",
  plexAgent = "PLEX_AGENT",
  userDefined = "USER_DEFINED",
  mlModel = "ML_MODEL"
}

export enum NodeType {
  actionEventTrigger = "actionEventTrigger",
  and = "and",
  buildObjectFromProperties = "buildObjectFromProperties",
  booleanConstant = "booleanConstant",
  callCustomFieldsGet = "callCustomFieldsGet",
  callCustomFieldsSearch = "callCustomFieldsSearch",
  callCustomFieldsUpdate = "callCustomFieldsUpdate",
  callCustomObjectAdd = "callCustomObjectAdd",
  callCustomObjectDelete = "callCustomObjectDelete",
  callCustomObjectGet = "callCustomObjectGet",
  callCustomObjectSearch = "callCustomObjectSearch",
  callCustomObjectUpdate = "callCustomObjectUpdate",
  callDataSource = "callDataSource",
  callEmailSend = "callEmailSend",
  callExternalApi = "callExternalApi",
  callMlModel = "callMlModel",
  callPlexAgent = "callPlexAgent",
  callPlexOpenApi = "callPlexOpenApi",
  coalesce = "coalesce",
  comment = "comment",
  compareDate = "compareDate",
  constantDateTime = "constantDateTime",
  compareNumber = "compareNumber",
  constant = "constant",
  constantBoolean = "constantBoolean",
  constantDate = "constantDate",
  constantDecimal = "constantDecimal",
  constantInteger = "constantInteger",
  constantNull = "constantNull",
  constantString = "constantString",
  constantTime = "constantTime",
  constantUuid = "constantUuid",
  convertToList = "convertToList",
  convertToNumber = "convertToNumber",
  convertToString = "convertToString",
  compareString = "compareString",
  convertBooleanToString = "convertBooleanToString",
  convertDateToString = "convertDateToString",
  convertNumberToString = "convertNumberToString",
  convertToReference = "convertToReference",
  dateDifference = "dateDifference",
  dateOffset = "dateOffset",
  dateRange = "dateRange",
  datePart = "datePart",
  dateTimeNow = "dateTimeNow",
  equalsNumber = "equalsNumber",
  eventTrigger = "eventTrigger",
  flowInputs = "flowInputs",
  forEach = "forEach",
  forEachBreak = "forEachBreak",
  forEachNext = "forEachNext",
  forEachStop = "forEachStop",
  greaterThanNumber = "greaterThanNumber",
  if = "if",
  isNullOrEmpty = "isNullOrEmpty",
  lessThanNumber = "lessThanNumber",
  listAdd = "listAdd",
  listCount = "listCount",
  listEmpty = "listEmpty",
  listItem = "listItem",
  listItemFirst = "listItemFirst",
  listSort = "listSort",
  not = "not",
  notEqualNumber = "notEqualNumber",
  nowConstant = "nowConstant",
  numberAdd = "numberAdd",
  numberArithmetic = "numberArithmetic",
  numberCompare = "numberCompare",
  numberDivide = "numberDivide",
  numberModulus = "numberModulus",
  numberMultiply = "numberMultiply",
  numberSubtract = "numberSubtract",
  selectProperties = "selectProperties",
  start = "start",
  stop = "stop",
  stringCompare = "stringCompare",
  stringConcat = "stringConcat",
  stringConcatenate = "stringConcatenate",
  stringConstant = "stringConstant",
  stringReplace = "stringReplace",
  objectProperties = "objectProperties",
  objectUpdate = "objectUpdate",
  or = "or",
  uuidToString = "uuidToString",
  textFormat = "textFormat"
}

export enum NodeOperationIcon {
  add = "add",
  create = "create",
  delete = "delete",
  get = "get",
  search = "search",
  send = "send",
  update = "update",
  zero = "zero"
}

export interface ISchemaSystem {
  createIo?: boolean;
  ioType?: IoType;
}

export type NodePropertyOptions =
  | IEnumOptions
  | IDataPropertyCountOptions
  | IDataTypeOptions
  | IEventPropertyOptions
  | IActionEventPropertyOptions
  | IFlowInputEditorOptions
  | ISchemaPickerOptions
  | ISchemaPropertyPickerOptions
  | IStandardObjectPickerOptions
  | ISourceApiPickerWithChildrenOptions
  | IConnectedSchemaTagsOptions
  | ITextOptions
  | ITemplateEditorOptions;

export interface INodePropertySchemaSourceOptions {
  sourceSystem: DocumentSchemaSource;
  dataParams: string[];
}

export const getSourceSystem = (
  schemaSourceSystemType: DocumentSchemaSource | INodePropertySchemaSourceOptions
): DocumentSchemaSource => {
  if ((schemaSourceSystemType as INodePropertySchemaSourceOptions).sourceSystem !== undefined) {
    return (schemaSourceSystemType as INodePropertySchemaSourceOptions).sourceSystem;
  }
  return schemaSourceSystemType as DocumentSchemaSource;
};

export const sourceSystemIncludesDataParam = (
  schemaSourceSystemType: DocumentSchemaSource | INodePropertySchemaSourceOptions,
  dataParam: string
): boolean => {
  if ((schemaSourceSystemType as INodePropertySchemaSourceOptions).dataParams !== undefined) {
    return (schemaSourceSystemType as INodePropertySchemaSourceOptions).dataParams.includes(dataParam);
  }
  return true;
};

export interface INodeProperty {
  name: string;
  label?: string;
  outsideFormEdit?: boolean;
  propertyType: NodeConfigPropertyType;
  sectionName?: string;
  sectionGroupName?: string;
  schemaSourceSystemType?: DocumentSchemaSource | INodePropertySchemaSourceOptions;
  schemaSystem?: ISchemaSystem;
  options?: NodePropertyOptions;
  clearPropertiesOnChange?: string[];
  showOnPropertyValues?: { propertyName: string; propertyValues: any[]; listExactMatch?: boolean };
}

export interface IControlFlowParam {
  name: string;
  label?: string;
  isError?: boolean;
  startsLoop?: boolean;
}

/** The definition for a node type. */
export interface INodeTypeDefinition {
  /** Name of the react component for this node type. */
  componentName: string;
  /** The unique id for this node type. */
  id: string;
  /** The {@link NodeTypeCategory} for this node type. */
  category: NodeTypeCategory;
  /** The order used to sort this node type in menus. */
  menuOrder: number;
  /** The label displayed for this node type, if different from the id. */
  label: string;
  /** The operation icon for this node type. */
  operationIcon?: NodeOperationIcon;
  /** A flag for if the label should show when anchored. */
  hideLabelOnAnchor: boolean;
  /** Which area node anchors to when no connection is made. */
  unconnectedAnchorLocation: AnchorArea | undefined;
  /** If there is a control input, but no control outputs. */
  controlInputOnly: boolean;
  /** The controlled flow order inputs for this node type. */
  controlInputs: IControlFlowParam[];
  /** If there is a control output, but no control inputs. */
  controlOutputOnly?: boolean;
  /** The controlled flow order outputs for this node type. */
  controlOutputs: IControlFlowParam[];
  /** If this node is only possible inside loops. */
  innerLoop: boolean;
  /** The data input {@link IDataParam} list for this node type on creation. */
  dataInputs: IDataParam[];
  /** The node returns an item from the list input. */
  listToItem: boolean;
  /** The list the data inputs are stored as in the document, or undefined if not stored as a list. */
  documentDataInputListName?: string;
  /** The data output {@link IDataParam} list for this node type on creation. */
  dataOutputs: IDataParam[];
  /** The list the data outputs are stored as in the document, or undefined if not stored as a list. */
  documentDataOutputList?: string;
  /** The outputs are the schema properties of input. */
  outputsSchemaProperties: boolean;
  /** The inputs are the schema properties of output.
   * identifiersOnly=true if only identifiers should be used.
   * excludeIdentifiers=true if identifiers should not be included.
   * forceOptional=true if all schema-derived inputs should be non-required optional toggles. */
  inputsSchemaProperties?: {
    identifiersOnly: boolean;
    schemaPropertyName: string;
    excludeIdentifiers?: boolean;
    forceOptional?: boolean;
  };
  /** The reference schemas change based on their connections. */
  adaptToReferenceSchemaOnConnection?: { resetOnDisconnect?: boolean };
  /** The user provided configurations for a node prior to running a flow */
  nodeConfigProperties: INodeProperty[];
  /** The size of the node on the canvas. */
  size: NodeSize;
  /** If the evaluate function will be skipped during execution. Only use this for special case node types. */
  skipEvaluate: boolean;
  /** A flag that denote the given node is support for experimental mode*/
  isExperimental: boolean;
  /** A flag that denotes the given node should prompt the user to confirm their intention to delete the node.  */
  confirmDeletion: boolean;
  /** The node is only available to be created in a flow when this feature toggle is on. */
  featureToggle?: string;
  /** The node is only available when this customer setting is enabled for the tenant. */
  setting?: string;
  /** The evaluation performed when running this node. */
  evaluate: (
    input: { [name: string]: string | number | boolean | object | object[] },
    nodeProperties?: any
  ) => {
    output: { [name: string]: string | number | boolean | object | object[] };
    activeControlHandleIds: string[];
  };
}

/** The definition for a node type. */
export interface ISetNodeTypeDefinitionParams {
  /** The node type. */
  nodeType: NodeType;
  /** The {@link NodeTypeCategory} for this node type. */
  category: NodeTypeCategory;
  /** The label displayed for this node type, if different from the id. */
  label?: string;
  /** The operation icon for this node type. */
  operationIcon?: NodeOperationIcon;
  /** A flag for if the label should show when anchored. */
  hideLabelOnAnchor?: boolean;
  /** Which area node anchors to when no connection is made. */
  unconnectedAnchorLocation?: AnchorArea | undefined;
  /** If there is a control input, but no control outputs. */
  controlInputOnly?: boolean;
  /** The controlled flow order inputs for this node type. */
  controlInputs?: IControlFlowParam[];
  /** If there is a control output, but no control inputs. */
  controlOutputOnly?: boolean;
  /** The controlled flow order outputs for this node type on creation. */
  controlOutputs: IControlFlowParam[];
  /** If this node is only possible inside loops. */
  innerLoop?: boolean;
  /** The data input {@link IDataParam} list for this node type on creation. */
  dataInputs: IDataParam[];
  /** The node returns an item from the list input. */
  listToItem?: boolean;
  /** The list the data inputs are stored as in the document, or undefined if not stored as a list. */
  documentDataInputListName?: string;
  /** The data output {@link IDataParam} list for this node type on creation. */
  dataOutputs: IDataParam[];
  /** The list the data outputs are stored as in the document, or undefined if not stored as a list. */
  documentDataOutputList?: string;
  /** The outputs are the schema properties of input. */
  outputsSchemaProperties?: boolean;
  /** The inputs are the schema properties of output. */
  inputsSchemaProperties?: {
    identifiersOnly: boolean;
    schemaPropertyName: string;
    excludeIdentifiers?: boolean;
    forceOptional?: boolean;
  };
  /** The reference schemas change based on their connections. */
  adaptToReferenceSchemaOnConnection?: { resetOnDisconnect?: boolean };
  /** The user provided configurations for a node prior to running a flow */
  nodeConfigProperties?: INodeProperty[];
  /** The size of the node on the canvas. */
  size?: NodeSize;
  /** If the evaluate function will be skipped during execution. Only use this for special case node types. */
  skipEvaluate?: boolean;
  /** A flag that denotes the given node is support for experimental mode*/
  isExperimental?: boolean;
  /** A flag that denotes the given node should prompt the user to confirm their intention to delete the node.  */
  confirmDeletion?: boolean;
  /** The node is only available to be created in a flow when this feature toggle is on. */
  featureToggle?: string;
  /** The node is only available when this customer setting is enabled for the tenant. */
  setting?: string;
  /** The evaluation performed when running this node. */
  evaluate: (
    input: { [name: string]: string | number | boolean | object | object[] },
    nodeProperties?: any
  ) => {
    output: { [name: string]: string | number | boolean | object | object[] };
    activeControlHandleIds: string[];
  };
}

export class NodeTypeDefinitions {
  private definitions: { [nodeTypeId: string]: INodeTypeDefinition };

  constructor() {
    this.definitions = {};
  }

  public getDefinition(id: string) {
    return this.definitions[id];
  }

  public getDefinitions() {
    return Object.keys(this.definitions).map((key) => this.definitions[key]);
  }

  public setDefinition(request: ISetNodeTypeDefinitionParams): void {
    const componentName = request.nodeType!.charAt(0).toUpperCase() + request.nodeType!.slice(1) + "Node";
    let id = componentName.replace("Node", "");
    id = id.charAt(0).toLowerCase() + id.slice(1);
    let readableId = id.replace(camelCaseToSpacesRegEx, "$1$4 $2$3$5");
    readableId = readableId.charAt(0).toUpperCase() + readableId.slice(1);

    let controlInputs: IControlFlowParam[] = request.controlInputs ?? [];
    if (
      !request.controlInputs &&
      !request.controlOutputOnly &&
      (request.controlInputOnly || request.controlOutputs?.length > 0)
    ) {
      controlInputs = [{ name: "control" }];
    }

    let definition: INodeTypeDefinition = {
      componentName: componentName,
      id: id,
      category: request.category,
      menuOrder: Object.keys(this.definitions).length,
      label: request.label ?? readableId,
      operationIcon: request.operationIcon,
      hideLabelOnAnchor: request.hideLabelOnAnchor ?? false,
      unconnectedAnchorLocation: request.unconnectedAnchorLocation,
      controlInputOnly: request.controlInputOnly ?? false,
      controlInputs: controlInputs,
      controlOutputOnly: request.controlOutputOnly ?? false,
      skipEvaluate: request.skipEvaluate ?? false,
      controlOutputs: request.controlOutputs,
      innerLoop: request.innerLoop ?? false,
      dataInputs: request.dataInputs,
      listToItem: request.listToItem ?? false,
      documentDataInputListName: request.documentDataInputListName,
      dataOutputs: request.dataOutputs,
      documentDataOutputList: request.documentDataOutputList,
      outputsSchemaProperties: request.outputsSchemaProperties ?? false,
      inputsSchemaProperties: request.inputsSchemaProperties,
      adaptToReferenceSchemaOnConnection: request.adaptToReferenceSchemaOnConnection,
      nodeConfigProperties: request.nodeConfigProperties ?? [],
      isExperimental: request.isExperimental ?? false,
      size: request.size ?? NodeSize.Normal,
      confirmDeletion: request.confirmDeletion ?? false,
      featureToggle: request.featureToggle,
      setting: request.setting,
      evaluate: request.evaluate
    };

    definition.controlOutputs.forEach((o) => {
      if (o.label === undefined) {
        let readableName = o.name.replace(camelCaseToSpacesRegEx, "$1$4 $2$3$5");
        readableName = readableName.charAt(0).toUpperCase() + readableName.slice(1);
        o.label = readableName;
      }
      if (o.isError === undefined) {
        o.isError = false;
      }
    });

    definition.dataInputs.forEach((i) => {
      if (i.enabled === undefined) {
        i.enabled = true;
      }
      if (i.required === undefined) {
        i.required = true;
      }
    });

    definition.dataOutputs.forEach((o) => {
      if (o.enabled === undefined) {
        o.enabled = true;
      }
      if (o.required === undefined) {
        o.required = true;
      }
    });

    this.definitions[definition.id] = definition;
  }
}

globalThis.nodeTypeDefinitions = new NodeTypeDefinitions();
